import { Box, Text, Avatar, VStack } from '@chakra-ui/react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts'
import type { DailyCanvasserStatsDto } from '../../../types/campaign'
import { assignCanvasserColors } from '../../../utils/canvasserColors'
import { ChartFrame, ChartLegend, chartTheme } from '../../shared/viz'
import { AuthedAvatarImage } from '../../shared'

interface Props {
  canvasserStats: DailyCanvasserStatsDto[]
}

export function CanvasserPieChart({ canvasserStats }: Props) {
  if (canvasserStats.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Text color="gray.500" fontSize="sm">No canvasser activity today</Text>
      </Box>
    )
  }

  // Sort by contribution (doors + businesses) descending so the top-7 colour
  // assignment and the "Other" collapse match visual prominence in the pie.
  const sorted = [...canvasserStats].sort(
    (a, b) => (b.doorsKnocked + b.businessesVisited) - (a.doorsKnocked + a.businessesVisited),
  )
  const slots = assignCanvasserColors(sorted)

  // The pie needs one slice per row, but the "Other" bucket returned by
  // assignCanvasserColors only carries a single representative canvasser —
  // aggregate the full tail's values into that one slice so the donut
  // matches the legend (one Other wedge, not one wedge per collapsed row).
  const otherTail = sorted.slice(slots.filter((s) => !s.isOther).length)
  const otherValue = otherTail.reduce((sum, c) => sum + c.doorsKnocked + c.businessesVisited, 0)

  const data = slots.map((slot) => {
    if (slot.isOther) {
      return {
        name: `Other (${otherTail.length})`,
        value: otherValue,
        color: slot.colorVar,
        portraitUrl: null as string | null,
        isOther: true,
      }
    }
    const c = slot.item
    return {
      name: c.fullName,
      value: c.doorsKnocked + c.businessesVisited,
      color: slot.colorVar,
      portraitUrl: c.portraitUrl,
      isOther: false,
    }
  })

  const legendItems = data.map((entry) => ({ label: entry.name, colorVar: entry.color }))
  const maxValue = Math.max(0, ...data.map((entry) => entry.value))

  return (
    <Box>
      <ChartFrame ariaLabel="Canvasser activity share" height={220}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={85}
            dataKey="value"
            nameKey="name"
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value} stops`, name]}
            contentStyle={chartTheme.tooltip.contentStyle}
          />
        </PieChart>
      </ChartFrame>

      <Box mt={2}>
        <ChartLegend variant="categorical" items={legendItems} />
      </Box>

      {/* Per-canvasser detail: avatar + share bar + stop count, same top-to-bottom
          order as the legend above so colour/name and avatar/value line up. The
          bar is proportional to the top canvasser and filled in the slice colour
          (it used to be an empty gap between avatar and value). */}
      <VStack gap={1} align="stretch" mt={2}>
        {data.map((entry, i) => {
          const pct = maxValue > 0 ? (entry.value / maxValue) * 100 : 0
          return (
            <Box key={i} display="flex" alignItems="center" gap={2} title={entry.name}>
              <Avatar.Root size="2xs"><Avatar.Fallback name={entry.name} /><AuthedAvatarImage src={entry.portraitUrl} /></Avatar.Root>
              <Box flex={1} h="6px" borderRadius="full" bg="border" overflow="hidden" aria-hidden="true">
                <Box
                  data-testid="canvasser-share-bar"
                  h="full"
                  borderRadius="full"
                  style={{ width: `${pct}%`, background: entry.color }}
                />
              </Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600">{entry.value}</Text>
            </Box>
          )
        })}
      </VStack>
    </Box>
  );
}
